import type { CredentialEvaluation } from '@dosfilos/domain';
import type { CredentialProgress } from '../../../../lib/api/credential-progress.api';

// ─── El expediente, listo para pintar (puro, con test) ──────────────────────
// Las pantallas solo traducen y dibujan: el orden, los agrupamientos y qué le
// falta a quién se deciden aquí.

export interface TermGroup<R> {
  termId: string;
  termName: string;
  rows: R[];
}

/** Agrupa por período RESPETANDO el orden que trae el servidor (cronológico):
 *  un período que reaparece más adelante abre otro grupo, nunca se reordena. */
export function groupByTerm<R extends { termId: string; termName: string }>(
  rows: readonly R[],
): TermGroup<R>[] {
  const groups: TermGroup<R>[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.termId === row.termId) last.rows.push(row);
    else groups.push({ termId: row.termId, termName: row.termName, rows: [row] });
  }
  return groups;
}

/** Puntos de una fila en la escala 4-3-2-1; sin nota A–F, una raya. */
export function formatPoints(points: number | null): string {
  return points === null ? '—' : points.toFixed(1);
}

/** Promedio con dos decimales; sin ninguna nota A–F, una raya. */
export function formatGpa(gpa: number | null): string {
  return gpa === null ? '—' : gpa.toFixed(2);
}

/** Lo que le falta a una credencial: obligatorios, cupos de grupos y unidades. */
export interface CredentialShortfall {
  requiredMissing: string[];
  groups: Array<{ courseCodes: string[]; shortfall: number }>;
  unitsMissing: number;
}

export function shortfallOf(c: CredentialEvaluation): CredentialShortfall {
  return {
    requiredMissing: c.requiredMissing,
    groups: c.groups
      .filter((g) => g.shortfall > 0)
      .map((g) => ({ courseCodes: g.courseCodes, shortfall: g.shortfall })),
    unitsMissing: c.unitsMissing,
  };
}

/** El registro de origen dice «graduado» y la regla vigente dice que no.
 *  Devuelve los DOS datos para mostrarlos juntos; ninguno corrige al otro.
 *  Contra la credencial más cercana (la que pide menos cursos). */
export interface GraduationMismatch {
  graduation: CredentialProgress['recordedGraduations'][number];
  credentialName: string;
  shortfall: CredentialShortfall;
}

/** Quién mira el expediente. `staff` es la ficha de la persona (todo);
 *  `student` es el alumno en su portal. */
export type RecordAudience = 'staff' | 'student';

/** Lo que el alumno NO ve de su propio expediente. Es información para el
 *  registro académico, no para él:
 *    · la discrepancia entre la graduación registrada y la regla vigente — un
 *      graduado leería que le falta algo para un título que ya recibió. Por eso,
 *      cuando la hay, tampoco ve las credenciales evaluadas con la regla: todas
 *      dirían «aún no cumple». La graduación sí la ve, como dato;
 *    · las anotaciones del sistema de origen (la de la graduación y la de cada
 *      nota: «original: A++», «See SP/92»), que son notas de trabajo.
 *  Pantalla y PDF pasan por aquí: el papel no puede decir más que el monitor. */
export function progressFor(p: CredentialProgress, audience: RecordAudience): CredentialProgress {
  if (audience === 'staff') return p;
  return {
    ...p,
    rows: p.rows.map((r) => ({ ...r, gradeNote: null })),
    recordedGraduations: p.recordedGraduations.map((g) => ({ ...g, note: null })),
    credentials: p.graduationMismatch ? [] : p.credentials,
    graduationMismatch: false,
  };
}

export function graduationMismatch(p: CredentialProgress): GraduationMismatch | null {
  const graduation = p.recordedGraduations[0];
  if (!p.graduationMismatch || !graduation || p.credentials.length === 0) return null;
  const closest = [...p.credentials].sort((a, b) => a.coursesMissing - b.coursesMissing)[0]!;
  return { graduation, credentialName: closest.name, shortfall: shortfallOf(closest) };
}
